// src/gpu/camera.ts
var Camera = class {
  constructor() {
    this.projection = identity();
    this.view = identity();
    this.viewProjection = identity();
    this.fovDeg = 45;
    this.aspect = 1;
    this.near = 0.1;
    this.far = 1e3;
    this.position = [0, 0, 1];
    this.target = [0, 0, 0];
    this.up = [0, 1, 0];
    this.updateMatrices();
  }
  /**
   * Resize the camera to match viewport dimensions.
   * Automatically positions camera so pixel units at z=0 map to screen pixels.
   */
  resize({ width, height }) {
    const aspect = Math.max(1e-6, width / Math.max(1, height));
    const fovDeg = this.fovDeg;
    this.setPerspective(fovDeg, aspect, this.near, this.far);
    const fovRad = fovDeg * Math.PI / 180;
    const camZ = 0.5 * height / Math.tan(fovRad / 2);
    this.lookAt([0, 0, camZ], [0, 0, 0]);
  }
  /**
   * Set perspective projection
   */
  setPerspective(fovDeg, aspect, near, far) {
    this.fovDeg = fovDeg;
    this.aspect = aspect;
    this.near = near;
    this.far = far;
    perspective(this.projection, fovDeg * Math.PI / 180, aspect, near, far);
    this.updateMatrices();
  }
  /**
   * Set orthographic projection for 2D/screen-space rendering
   */
  setOrtho2D(width, height) {
    ortho(this.projection, 0, width, height, 0, -1, 1);
    setIdentity(this.view);
    this.updateMatrices();
  }
  /**
   * Position camera with look-at transform
   */
  lookAt(eye, target, up = this.up) {
    this.position = [eye[0], eye[1], eye[2]];
    this.target = [target[0], target[1], target[2]];
    this.up = [up[0], up[1], up[2]];
    lookAtMatrix(this.view, eye, target, up);
    this.updateMatrices();
  }
  /**
   * Recalculate the combined viewProjection matrix
   */
  updateMatrices() {
    multiply(this.viewProjection, this.projection, this.view);
  }
};
function identity() {
  const m = new Float32Array(16);
  m[0] = 1;
  m[5] = 1;
  m[10] = 1;
  m[15] = 1;
  return m;
}
function setIdentity(out) {
  out[0] = 1;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = 1;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 1;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
}
function perspective(out, fovy, aspect, near, far) {
  const f = 1 / Math.tan(fovy / 2);
  out[0] = f / aspect;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = f;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = far === Infinity ? -1 : (far + near) / (near - far);
  out[11] = -1;
  out[12] = 0;
  out[13] = 0;
  out[14] = far === Infinity ? -2 * near : 2 * far * near / (near - far);
  out[15] = 1;
}
function ortho(out, left, right, bottom, top, near, far) {
  const lr = 1 / (left - right);
  const bt = 1 / (bottom - top);
  const nf = 1 / (near - far);
  out[0] = -2 * lr;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = -2 * bt;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 2 * nf;
  out[11] = 0;
  out[12] = (left + right) * lr;
  out[13] = (top + bottom) * bt;
  out[14] = (far + near) * nf;
  out[15] = 1;
}
function normalize(v) {
  const x = v[0], y = v[1], z = v[2];
  const len = Math.hypot(x, y, z) || 1;
  return [x / len, y / len, z / len];
}
function subtract(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}
function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function lookAtMatrix(out, eye, center, up) {
  const zAxis = normalize(subtract(eye, center));
  const xAxis = normalize(cross(up, zAxis));
  const yAxis = cross(zAxis, xAxis);
  out[0] = xAxis[0];
  out[4] = xAxis[1];
  out[8] = xAxis[2];
  out[12] = -dot(xAxis, eye);
  out[1] = yAxis[0];
  out[5] = yAxis[1];
  out[9] = yAxis[2];
  out[13] = -dot(yAxis, eye);
  out[2] = zAxis[0];
  out[6] = zAxis[1];
  out[10] = zAxis[2];
  out[14] = -dot(zAxis, eye);
  out[3] = 0;
  out[7] = 0;
  out[11] = 0;
  out[15] = 1;
}
function multiply(out, a, b) {
  const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
  const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
  const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
  const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  const b00 = b[0], b01 = b[1], b02 = b[2], b03 = b[3];
  const b10 = b[4], b11 = b[5], b12 = b[6], b13 = b[7];
  const b20 = b[8], b21 = b[9], b22 = b[10], b23 = b[11];
  const b30 = b[12], b31 = b[13], b32 = b[14], b33 = b[15];
  out[0] = a00 * b00 + a10 * b01 + a20 * b02 + a30 * b03;
  out[1] = a01 * b00 + a11 * b01 + a21 * b02 + a31 * b03;
  out[2] = a02 * b00 + a12 * b01 + a22 * b02 + a32 * b03;
  out[3] = a03 * b00 + a13 * b01 + a23 * b02 + a33 * b03;
  out[4] = a00 * b10 + a10 * b11 + a20 * b12 + a30 * b13;
  out[5] = a01 * b10 + a11 * b11 + a21 * b12 + a31 * b13;
  out[6] = a02 * b10 + a12 * b11 + a22 * b12 + a32 * b13;
  out[7] = a03 * b10 + a13 * b11 + a23 * b12 + a33 * b13;
  out[8] = a00 * b20 + a10 * b21 + a20 * b22 + a30 * b23;
  out[9] = a01 * b20 + a11 * b21 + a21 * b22 + a31 * b23;
  out[10] = a02 * b20 + a12 * b21 + a22 * b22 + a32 * b23;
  out[11] = a03 * b20 + a13 * b21 + a23 * b22 + a33 * b23;
  out[12] = a00 * b30 + a10 * b31 + a20 * b32 + a30 * b33;
  out[13] = a01 * b30 + a11 * b31 + a21 * b32 + a31 * b33;
  out[14] = a02 * b30 + a12 * b31 + a22 * b32 + a32 * b33;
  out[15] = a03 * b30 + a13 * b31 + a23 * b32 + a33 * b33;
}
var Mat4 = {
  identity,
  setIdentity,
  perspective,
  ortho,
  lookAt: lookAtMatrix,
  multiply
};
var Vec3Utils = {
  normalize,
  subtract,
  cross,
  dot
};

// src/gpu/device.ts
function isWebGPUSupported() {
  return typeof navigator !== "undefined" && "gpu" in navigator;
}
async function initGPU(canvas) {
  if (!isWebGPUSupported()) {
    console.warn("[gpu] WebGPU not supported, falling back to WebGL");
    return null;
  }
  try {
    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: "high-performance"
    });
    if (!adapter) {
      console.warn("[gpu] No suitable GPU adapter found");
      return null;
    }
    const device = await adapter.requestDevice({
      requiredFeatures: [],
      requiredLimits: {}
    });
    device.lost.then((info) => {
      console.error("[gpu] Device lost:", info.message);
      if (info.reason !== "destroyed") {
      }
    });
    const context = canvas.getContext("webgpu");
    if (!context) {
      console.warn("[gpu] Failed to get WebGPU canvas context");
      return null;
    }
    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({
      device,
      format,
      alphaMode: "premultiplied"
    });
    console.debug("[gpu] WebGPU initialized successfully");
    return {
      adapter,
      device,
      canvas,
      context,
      format
    };
  } catch (error) {
    console.error("[gpu] Failed to initialize WebGPU:", error);
    return null;
  }
}
function destroyGPU(ctx) {
  try {
    ctx.context.unconfigure();
    ctx.device.destroy();
  } catch {
  }
}

// src/gpu/geometry.ts
var QUAD_VERTICES = new Float32Array([
  // position    uv
  0,
  0,
  0,
  0,
  // bottom-left
  1,
  0,
  1,
  0,
  // bottom-right
  0,
  1,
  0,
  1,
  // top-left
  1,
  1,
  1,
  1
  // top-right
]);
var QUAD_INDICES = new Uint16Array([0, 1, 2, 2, 1, 3]);
function createQuadGeometry(device) {
  const vertexBuffer = device.createBuffer({
    label: "Quad Vertex Buffer",
    size: QUAD_VERTICES.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
  });
  device.queue.writeBuffer(vertexBuffer, 0, QUAD_VERTICES);
  const indexBuffer = device.createBuffer({
    label: "Quad Index Buffer",
    size: QUAD_INDICES.byteLength,
    usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST
  });
  device.queue.writeBuffer(indexBuffer, 0, QUAD_INDICES);
  return {
    vertexBuffer,
    indexBuffer,
    vertexCount: 4,
    indexCount: 6
  };
}
var VERTEX_BUFFER_LAYOUT = {
  arrayStride: 16,
  // 4 floats * 4 bytes
  stepMode: "vertex",
  attributes: [
    {
      // position
      shaderLocation: 0,
      offset: 0,
      format: "float32x2"
    },
    {
      // uv
      shaderLocation: 1,
      offset: 8,
      format: "float32x2"
    }
  ]
};
function destroyGeometry(geometry) {
  geometry.vertexBuffer.destroy();
  geometry.indexBuffer.destroy();
}

// src/gpu/shaders/image.wgsl
var image_default = "// Image Plane Shader\n// Renders textured quads with opacity, reveal animation, aspect ratio correction,\n// rounded corners, and drop shadows\n\nstruct Uniforms {\n  resolution: vec2f,\n  _pad0: vec2f,\n  translate: vec3f,\n  _pad1: f32,\n  scale: vec2f,\n  alpha: f32,\n  reveal: f32,\n  texAspect: f32,\n  containerAspect: f32,\n  borderRadius: f32,\n  shadowBlur: f32,\n  shadowOffset: vec2f,\n  _pad2: vec2f,\n  shadowColor: vec4f,\n  rotation: vec3f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var texSampler: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\nstruct VertexInput {\n  @location(0) position: vec2f,\n  @location(1) uv: vec2f,\n}\n\nstruct VertexOutput {\n  @builtin(position) position: vec4f,\n  @location(0) uv: vec2f,\n}\n\n// Signed distance to rounded rectangle\n// p: point in rectangle space (0 to size)\n// size: rectangle dimensions\n// radius: corner radius\nfn sdRoundedRect(p: vec2f, size: vec2f, radius: f32) -> f32 {\n  let r = min(radius, min(size.x, size.y) * 0.5);\n  let q = abs(p - size * 0.5) - size * 0.5 + r;\n  return length(max(q, vec2f(0.0))) + min(max(q.x, q.y), 0.0) - r;\n}\n\n@vertex\nfn vs_main(input: VertexInput) -> VertexOutput {\n  var output: VertexOutput;\n\n  // Transform position: scale, rotate in 3D around center, then translate\n  let scaled = input.position * uniforms.scale;\n  let center = uniforms.scale * 0.5;\n  var p = vec3f(scaled.x - center.x, scaled.y - center.y, 0.0);\n\n  // Rotate around X axis\n  let cx = cos(uniforms.rotation.x);\n  let sx = sin(uniforms.rotation.x);\n  p = vec3f(p.x, p.y * cx - p.z * sx, p.y * sx + p.z * cx);\n\n  // Rotate around Y axis\n  let cy = cos(uniforms.rotation.y);\n  let sy = sin(uniforms.rotation.y);\n  p = vec3f(p.x * cy + p.z * sy, p.y, -p.x * sy + p.z * cy);\n\n  // Rotate around Z axis\n  let cz = cos(uniforms.rotation.z);\n  let sz = sin(uniforms.rotation.z);\n  p = vec3f(p.x * cz - p.y * sz, p.x * sz + p.y * cz, p.z);\n\n  // Perspective projection\n  let perspective = 800.0;\n  let factor = perspective / (perspective + p.z);\n  let pos = vec2f(p.x * factor, p.y * factor) + center + uniforms.translate.xy;\n\n  // Convert to normalized device coordinates\n  let zeroToOne = pos / uniforms.resolution;\n  let zeroToTwo = zeroToOne * 2.0;\n  let clip = zeroToTwo - 1.0;\n\n  // Flip Y for WebGPU coordinate system\n  output.position = vec4f(clip.x, -clip.y, 0.0, 1.0);\n  output.uv = input.uv;\n\n  return output;\n}\n\n@fragment\nfn fs_main(input: VertexOutput) -> @location(0) vec4f {\n  var uv = input.uv;\n\n  // Aspect ratio correction (cover mode)\n  if (uniforms.texAspect > uniforms.containerAspect) {\n    let scale = uniforms.containerAspect / uniforms.texAspect;\n    uv.x = uv.x * scale + (1.0 - scale) * 0.5;\n  } else {\n    let scale = uniforms.texAspect / uniforms.containerAspect;\n    uv.y = uv.y * scale + (1.0 - scale) * 0.5;\n  }\n\n  // Sample texture\n  let color = textureSample(tex, texSampler, uv);\n\n  // Calculate position in plane space (pixels)\n  let planePos = input.uv * uniforms.scale;\n\n  // SDF for rounded corners\n  let dist = sdRoundedRect(planePos, uniforms.scale, uniforms.borderRadius);\n\n  // Smooth edge for anti-aliasing (1 pixel transition)\n  let edgeAlpha = 1.0 - smoothstep(-1.0, 0.0, dist);\n\n  // Shadow calculation (rendered behind, uses offset SDF)\n  var shadowAlpha = 0.0;\n  if (uniforms.shadowBlur > 0.0 || length(uniforms.shadowOffset) > 0.0) {\n    let shadowPos = planePos - uniforms.shadowOffset;\n    let shadowDist = sdRoundedRect(shadowPos, uniforms.scale, uniforms.borderRadius);\n    // Shadow fade based on blur radius\n    shadowAlpha = 1.0 - smoothstep(-uniforms.shadowBlur, uniforms.shadowBlur, shadowDist);\n    shadowAlpha *= uniforms.shadowColor.a;\n  }\n\n  // Apply reveal mask (bottom-to-top wipe)\n  let mask = step(1.0 - uniforms.reveal, input.uv.y);\n\n  // Combine: plane with rounded corners and shadow\n  let planeAlpha = color.a * uniforms.alpha * mask * edgeAlpha;\n\n  // Blend shadow under the plane\n  var finalColor = color.rgb;\n  var finalAlpha = planeAlpha;\n\n  // Mix shadow where plane is transparent\n  if (shadowAlpha > 0.0 && planeAlpha < 1.0) {\n    let shadowContrib = shadowAlpha * (1.0 - planeAlpha);\n    finalColor = mix(finalColor * planeAlpha, uniforms.shadowColor.rgb, shadowContrib) / max(planeAlpha + shadowContrib, 0.001);\n    finalAlpha = planeAlpha + shadowContrib;\n  }\n\n  return vec4f(finalColor, finalAlpha);\n}\n";

// src/gpu/pipeline.ts
async function createImagePipeline(device, format) {
  const shaderModule = device.createShaderModule({
    label: "Image Shader",
    code: image_default
  });
  const bindGroupLayout = device.createBindGroupLayout({
    label: "Image Bind Group Layout",
    entries: [
      {
        // Uniforms
        binding: 0,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
        buffer: {
          type: "uniform",
          hasDynamicOffset: true,
          minBindingSize: 256
        }
      },
      {
        // Sampler
        binding: 1,
        visibility: GPUShaderStage.FRAGMENT,
        sampler: { type: "filtering" }
      },
      {
        // Texture
        binding: 2,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: "float" }
      }
    ]
  });
  const pipelineLayout = device.createPipelineLayout({
    label: "Image Pipeline Layout",
    bindGroupLayouts: [bindGroupLayout]
  });
  const pipeline = await device.createRenderPipelineAsync({
    label: "Image Render Pipeline",
    layout: pipelineLayout,
    vertex: {
      module: shaderModule,
      entryPoint: "vs_main",
      buffers: [VERTEX_BUFFER_LAYOUT]
    },
    fragment: {
      module: shaderModule,
      entryPoint: "fs_main",
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            },
            alpha: {
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
              operation: "add"
            }
          },
          writeMask: GPUColorWrite.ALL
        }
      ]
    },
    primitive: {
      topology: "triangle-list",
      cullMode: "none"
    }
  });
  return { pipeline, bindGroupLayout };
}
function createPlaneBindGroup(device, layout, uniformBuffer, sampler, textureView) {
  return device.createBindGroup({
    label: "Plane Bind Group",
    layout,
    entries: [
      {
        binding: 0,
        resource: {
          buffer: uniformBuffer,
          size: 256
          // One uniform block
        }
      },
      {
        binding: 1,
        resource: sampler
      },
      {
        binding: 2,
        resource: textureView
      }
    ]
  });
}

// src/gpu/texture.ts
async function createTextureFromImage(device, image, options = {}) {
  const { label = "Image Texture", flipY = false } = options;
  if (image instanceof HTMLImageElement) {
    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      console.warn("[createTextureFromImage] Image has 0 dimensions");
      return null;
    }
  } else if (image.width === 0 || image.height === 0) {
    console.warn("[createTextureFromImage] ImageBitmap has 0 dimensions");
    return null;
  }
  let bitmap;
  if (image instanceof ImageBitmap) {
    bitmap = image;
  } else {
    bitmap = await createImageBitmap(image, {
      imageOrientation: flipY ? "flipY" : "none"
    });
  }
  if (bitmap.width === 0 || bitmap.height === 0) {
    console.warn(
      "[createTextureFromImage] Bitmap has 0 dimensions after conversion"
    );
    bitmap.close();
    return null;
  }
  const texture = device.createTexture({
    label,
    size: [bitmap.width, bitmap.height, 1],
    format: "rgba8unorm",
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT
  });
  device.queue.copyExternalImageToTexture({ source: bitmap }, { texture }, [
    bitmap.width,
    bitmap.height
  ]);
  bitmap.close();
  return texture;
}
async function loadTexture(device, url, options = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = async () => {
      try {
        const texture = await createTextureFromImage(device, img, options);
        resolve(texture);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error(`Failed to load texture: ${url}`));
    img.src = url;
  });
}
function createSampler(device, options = {}) {
  const {
    label = "Default Sampler",
    filter = "linear",
    addressMode = "clamp-to-edge"
  } = options;
  return device.createSampler({
    label,
    magFilter: filter,
    minFilter: filter,
    addressModeU: addressMode,
    addressModeV: addressMode
  });
}
function createPlaceholderTexture(device, color = [0, 0, 0, 0]) {
  const texture = device.createTexture({
    label: "Placeholder Texture",
    size: [1, 1, 1],
    format: "rgba8unorm",
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
  });
  const data = new Uint8Array(color.map((c) => Math.floor(c * 255)));
  device.queue.writeTexture({ texture }, data, { bytesPerRow: 4 }, [1, 1, 1]);
  return texture;
}

// src/gpu/types.ts
var UNIFORM_STRIDE = 256;

// src/gpu/uniforms.ts
function createUniformBuffer(device, maxPlanes) {
  const size = maxPlanes * UNIFORM_STRIDE;
  const buffer = device.createBuffer({
    label: "Plane Uniforms Buffer",
    size,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
  });
  const data = new Float32Array(maxPlanes * (UNIFORM_STRIDE / 4));
  return {
    buffer,
    data,
    capacity: maxPlanes
  };
}
function writeUniform(uniformBuffer, index, uniform) {
  if (index >= uniformBuffer.capacity) {
    console.warn("[uniforms] Index exceeds buffer capacity");
    return;
  }
  const offset = index * (UNIFORM_STRIDE / 4);
  const data = uniformBuffer.data;
  data[offset + 0] = uniform.resolution[0];
  data[offset + 1] = uniform.resolution[1];
  data[offset + 2] = 0;
  data[offset + 3] = 0;
  data[offset + 4] = uniform.translate[0];
  data[offset + 5] = uniform.translate[1];
  data[offset + 6] = uniform.translate[2];
  data[offset + 7] = 0;
  data[offset + 8] = uniform.scale[0];
  data[offset + 9] = uniform.scale[1];
  data[offset + 10] = uniform.alpha;
  data[offset + 11] = uniform.reveal;
  data[offset + 12] = uniform.texAspect;
  data[offset + 13] = uniform.containerAspect;
  data[offset + 14] = uniform.borderRadius;
  data[offset + 15] = uniform.shadowBlur;
  data[offset + 16] = uniform.shadowOffset[0];
  data[offset + 17] = uniform.shadowOffset[1];
  data[offset + 18] = 0;
  data[offset + 19] = 0;
  data[offset + 20] = uniform.shadowColor[0];
  data[offset + 21] = uniform.shadowColor[1];
  data[offset + 22] = uniform.shadowColor[2];
  data[offset + 23] = uniform.shadowColor[3];
  data[offset + 24] = uniform.rotation[0];
  data[offset + 25] = uniform.rotation[1];
  data[offset + 26] = uniform.rotation[2];
}
function uploadUniforms(device, uniformBuffer, count) {
  const byteSize = count * UNIFORM_STRIDE;
  device.queue.writeBuffer(
    uniformBuffer.buffer,
    0,
    uniformBuffer.data.buffer,
    0,
    byteSize
  );
}
function destroyUniformBuffer(uniformBuffer) {
  uniformBuffer.buffer.destroy();
}

// src/gpu/renderer.ts
var MAX_PLANES = 128;
var GPURenderer = class {
  constructor(config) {
    this.ctx = null;
    this.geometry = null;
    this.pipeline = null;
    this.uniformBuffer = null;
    this.sampler = null;
    this.placeholderTexture = null;
    this.canvasSize = { width: 0, height: 0 };
    this.devicePixelRatio = 1;
    /** Bind group cache: keyed by texture + sampler to avoid per-frame allocation */
    this.bindGroupCache = /* @__PURE__ */ new Map();
    this.bindGroupIdCounter = 0;
    this.textureIdMap = /* @__PURE__ */ new WeakMap();
    this.samplerIdMap = /* @__PURE__ */ new WeakMap();
    this.needsRedraw = true;
    this.extraFrames = 0;
    this.maxPixelRatio = config.maxPixelRatio ?? 2;
  }
  /**
   * Check if WebGPU is available
   */
  static isSupported() {
    return isWebGPUSupported();
  }
  /**
   * Initialize the GPU renderer
   */
  async init(canvas) {
    this.ctx = await initGPU(canvas);
    if (!this.ctx) return false;
    const { device, format } = this.ctx;
    this.geometry = createQuadGeometry(device);
    this.pipeline = await createImagePipeline(device, format);
    this.uniformBuffer = createUniformBuffer(device, MAX_PLANES);
    this.sampler = createSampler(device);
    this.placeholderTexture = createPlaceholderTexture(device);
    this.resize();
    return true;
  }
  /**
   * Resize the canvas and viewport
   */
  resize() {
    if (!this.ctx) return;
    const { canvas, context, device, format } = this.ctx;
    const cssWidth = Math.floor(canvas.clientWidth);
    const cssHeight = Math.floor(canvas.clientHeight);
    if (cssWidth === 0 || cssHeight === 0) {
      return;
    }
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(rawDpr, this.maxPixelRatio);
    const pixelWidth = Math.max(1, Math.floor(cssWidth * dpr));
    const pixelHeight = Math.max(1, Math.floor(cssHeight * dpr));
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      context.configure({
        device,
        format,
        alphaMode: "premultiplied"
      });
      this.canvasSize.width = pixelWidth;
      this.canvasSize.height = pixelHeight;
      this.devicePixelRatio = dpr;
      this.needsRedraw = true;
    }
  }
  /**
   * Get a unique ID for a GPU resource, assigning one if needed.
   */
  getTextureId(tex) {
    let id = this.textureIdMap.get(tex);
    if (id === void 0) {
      id = this.bindGroupIdCounter++;
      this.textureIdMap.set(tex, id);
    }
    return id;
  }
  getSamplerId(s) {
    let id = this.samplerIdMap.get(s);
    if (id === void 0) {
      id = this.bindGroupIdCounter++;
      this.samplerIdMap.set(s, id);
    }
    return id;
  }
  /**
   * Get or create a cached bind group for a plane's texture+sampler.
   */
  getCachedBindGroup(device, texture, sampler) {
    const texId = this.getTextureId(texture);
    const samId = this.getSamplerId(sampler);
    const key = `${texId}:${samId}`;
    const cached = this.bindGroupCache.get(key);
    if (cached && cached.texture === texture && cached.sampler === sampler) {
      return cached.bindGroup;
    }
    const textureView = texture.createView();
    const bindGroup = createPlaneBindGroup(
      device,
      this.pipeline.bindGroupLayout,
      this.uniformBuffer.buffer,
      sampler,
      textureView
    );
    this.bindGroupCache.set(key, { bindGroup, texture, sampler });
    return bindGroup;
  }
  /**
   * Render a scene
   */
  render(scene) {
    if (!this.ctx || !this.geometry || !this.pipeline || !this.uniformBuffer) {
      return;
    }
    if (this.canvasSize.width === 0 || this.canvasSize.height === 0) {
      return;
    }
    if (!this.needsRedraw && this.extraFrames === 0) return;
    this.needsRedraw = false;
    this.extraFrames = Math.max(0, this.extraFrames - 1);
    const { device, context } = this.ctx;
    const dpr = this.devicePixelRatio;
    const textureView = context.getCurrentTexture().createView();
    const encoder = device.createCommandEncoder({
      label: "Render Encoder"
    });
    const pass = encoder.beginRenderPass({
      label: "Main Render Pass",
      colorAttachments: [
        {
          view: textureView,
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
          loadOp: "clear",
          storeOp: "store"
        }
      ]
    });
    pass.setPipeline(this.pipeline.pipeline);
    pass.setVertexBuffer(0, this.geometry.vertexBuffer);
    pass.setIndexBuffer(this.geometry.indexBuffer, "uint16");
    const planes = scene.planes;
    for (let i = 0; i < planes.length; i++) {
      const plane = planes[i];
      if (!plane) continue;
      const bounds = plane.bounds;
      if (!bounds) continue;
      const uniform = {
        resolution: [this.canvasSize.width, this.canvasSize.height],
        translate: [bounds.x * dpr, bounds.y * dpr, bounds.z],
        scale: [bounds.w * dpr, bounds.h * dpr],
        alpha: plane.opacity,
        reveal: plane.reveal,
        texAspect: plane.aspectRatio || 1,
        containerAspect: bounds.w / bounds.h,
        // Border and shadow - read from plane or use defaults
        borderRadius: (plane.borderRadius ?? 0) * dpr,
        shadowBlur: (plane.shadowBlur ?? 0) * dpr,
        shadowOffset: [
          (plane.shadowOffset?.[0] ?? 0) * dpr,
          (plane.shadowOffset?.[1] ?? 0) * dpr
        ],
        shadowColor: plane.shadowColor ?? [0, 0, 0, 0],
        rotation: plane.rotation ?? [0, 0, 0]
      };
      writeUniform(this.uniformBuffer, i, uniform);
      const texture = plane.texture ?? this.placeholderTexture;
      if (!texture) continue;
      const sampler = plane.sampler ?? this.sampler;
      const bindGroup = this.getCachedBindGroup(device, texture, sampler);
      const dynamicOffset = i * 256;
      pass.setBindGroup(0, bindGroup, [dynamicOffset]);
      pass.drawIndexed(this.geometry.indexCount);
    }
    if (planes.length > 0) {
      uploadUniforms(device, this.uniformBuffer, planes.length);
    }
    pass.end();
    device.queue.submit([encoder.finish()]);
  }
  /**
   * Render a scene to a texture view (for post-processing pipeline)
   */
  renderToTexture(scene, encoder, targetView) {
    if (!this.ctx || !this.geometry || !this.pipeline || !this.uniformBuffer) {
      return;
    }
    if (this.canvasSize.width === 0 || this.canvasSize.height === 0) {
      return;
    }
    const { device } = this.ctx;
    const dpr = this.devicePixelRatio;
    const pass = encoder.beginRenderPass({
      label: "Scene Render Pass",
      colorAttachments: [
        {
          view: targetView,
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
          loadOp: "clear",
          storeOp: "store"
        }
      ]
    });
    pass.setPipeline(this.pipeline.pipeline);
    pass.setVertexBuffer(0, this.geometry.vertexBuffer);
    pass.setIndexBuffer(this.geometry.indexBuffer, "uint16");
    const planes = scene.planes;
    for (let i = 0; i < planes.length; i++) {
      const plane = planes[i];
      if (!plane) continue;
      const bounds = plane.bounds;
      if (!bounds) continue;
      const uniform = {
        resolution: [this.canvasSize.width, this.canvasSize.height],
        translate: [bounds.x * dpr, bounds.y * dpr, bounds.z],
        scale: [bounds.w * dpr, bounds.h * dpr],
        alpha: plane.opacity,
        reveal: plane.reveal,
        texAspect: plane.aspectRatio || 1,
        containerAspect: bounds.w / bounds.h,
        borderRadius: (plane.borderRadius ?? 0) * dpr,
        shadowBlur: (plane.shadowBlur ?? 0) * dpr,
        shadowOffset: [
          (plane.shadowOffset?.[0] ?? 0) * dpr,
          (plane.shadowOffset?.[1] ?? 0) * dpr
        ],
        shadowColor: plane.shadowColor ?? [0, 0, 0, 0],
        rotation: plane.rotation ?? [0, 0, 0]
      };
      writeUniform(this.uniformBuffer, i, uniform);
      const texture = plane.texture ?? this.placeholderTexture;
      if (!texture) continue;
      const sampler = plane.sampler ?? this.sampler;
      const bindGroup = this.getCachedBindGroup(device, texture, sampler);
      const dynamicOffset = i * 256;
      pass.setBindGroup(0, bindGroup, [dynamicOffset]);
      pass.drawIndexed(this.geometry.indexCount);
    }
    if (planes.length > 0) {
      uploadUniforms(device, this.uniformBuffer, planes.length);
    }
    pass.end();
  }
  /**
   * Request a redraw on the next frame
   */
  requestRedraw() {
    this.needsRedraw = true;
    this.extraFrames = Math.max(this.extraFrames, 2);
  }
  /**
   * Get the GPU device (for creating textures externally)
   */
  getDevice() {
    return this.ctx?.device ?? null;
  }
  /**
   * Get the canvas context
   */
  getContext() {
    return this.ctx?.context ?? null;
  }
  /**
   * Get the texture format
   */
  getFormat() {
    return this.ctx?.format ?? null;
  }
  /**
   * Get the canvas size
   */
  getCanvasSize() {
    return { ...this.canvasSize };
  }
  /**
   * Get the default sampler (for planes that don't have their own)
   */
  getDefaultSampler() {
    return this.sampler;
  }
  /**
   * Clean up all GPU resources
   */
  destroy() {
    if (this.geometry) destroyGeometry(this.geometry);
    if (this.uniformBuffer) destroyUniformBuffer(this.uniformBuffer);
    if (this.placeholderTexture) this.placeholderTexture.destroy();
    if (this.ctx) destroyGPU(this.ctx);
    this.bindGroupCache.clear();
    this.ctx = null;
    this.geometry = null;
    this.pipeline = null;
    this.uniformBuffer = null;
    this.sampler = null;
    this.placeholderTexture = null;
  }
};

// src/gpu/gl/geometry.ts
var GLGeometry = class {
  constructor(gl) {
    this.gl = gl;
    this.vbo = null;
    this.tbo = null;
    this.ibo = null;
    this.count = 0;
    if (!gl) return;
    const positions = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);
    const uvs = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);
    const indices = new Uint16Array([0, 1, 2, 2, 1, 3]);
    this.vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    this.tbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.tbo);
    gl.bufferData(gl.ARRAY_BUFFER, uvs, gl.STATIC_DRAW);
    this.ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    this.count = indices.length;
  }
  draw(pgm) {
    const gl = this.gl;
    if (!gl || !pgm || !pgm.prog) return;
    if (!this.vbo || !this.tbo || !this.ibo) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enableVertexAttribArray(pgm.a_position);
    gl.vertexAttribPointer(pgm.a_position, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.tbo);
    gl.enableVertexAttribArray(pgm.a_uv);
    gl.vertexAttribPointer(pgm.a_uv, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.drawElements(gl.TRIANGLES, this.count, gl.UNSIGNED_SHORT, 0);
  }
  destroy() {
    const gl = this.gl;
    if (!gl) return;
    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.tbo) gl.deleteBuffer(this.tbo);
    if (this.ibo) gl.deleteBuffer(this.ibo);
  }
};

// src/gpu/gl/shaders.ts
var IMAGE_SHADER = {
  vertex: `
    #define PI 3.1415926535897932384626433832795
    precision highp float;
    attribute vec2 a_position;
    attribute vec2 a_uv;
    uniform vec3 u_translate;
    uniform vec2 u_scale;
    uniform mat4 modelViewMatrix;
    uniform mat4 projectionMatrix;
    uniform float u_strength;
    uniform float u_tilt;
    uniform vec2 u_resolution;
    varying vec2 v_uv;
    void main() {
      vec2 pos = a_position * u_scale + u_translate.xy;
      vec2 centered = vec2(
        pos.x - u_resolution.x * 0.5,
        -(pos.y - u_resolution.y * 0.5)
      );
      vec3 worldPos = vec3(centered, u_translate.z);
      float bottomWorldY = -((u_translate.y + u_scale.y) - u_resolution.y * 0.5);
      float s = sin(u_tilt);
      float c = cos(u_tilt);
      worldPos.y -= bottomWorldY;
      float y0 = worldPos.y;
      float z0 = worldPos.z;
      worldPos.y = y0 * c;
      worldPos.z = z0 - y0 * s;
      worldPos.y += bottomWorldY;
      vec4 viewPos = modelViewMatrix * vec4(worldPos, 1.0);
      float phase = viewPos.x / max(1.0, u_resolution.x) * PI + PI * 0.5;
      float ampZ = min(u_scale.x, u_scale.y) * 0.5;
      viewPos.z += -abs(sin(phase) * u_strength) * ampZ;
      gl_Position = projectionMatrix * viewPos;
      v_uv = a_uv;
    }
  `,
  fragment: `
    precision mediump float;
    uniform sampler2D u_tex0;
    uniform float u_alpha;
    uniform float u_zoom;
    uniform float u_reveal;
    uniform float u_texAspect;
    uniform float u_containerAspect;
    varying vec2 v_uv;
    void main() {
      vec2 uv = v_uv;
      // Aspect ratio correction (cover mode)
      if (u_texAspect > u_containerAspect) {
        float scale = u_containerAspect / u_texAspect;
        uv.x = uv.x * scale + (1.0 - scale) * 0.5;
      } else {
        float scale = u_texAspect / u_containerAspect;
        uv.y = uv.y * scale + (1.0 - scale) * 0.5;
      }
      // Zoom effect
      float s = clamp(1.0 - u_zoom, 0.0, 1.0);
      uv = (uv - 0.5) * s + 0.5;
      vec4 c = texture2D(u_tex0, uv);
      // Reveal mask (bottom-to-top wipe)
      float mask = step(1.0 - v_uv.y, u_reveal);
      gl_FragColor = vec4(c.rgb, c.a * u_alpha * mask);
    }
  `
};

// src/gpu/gl/utils.ts
function createShader(gl, type, src) {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  const ok = gl.getShaderParameter(sh, gl.COMPILE_STATUS);
  if (!ok) {
    const log = gl.getShaderInfoLog(sh) || "";
    console.error("[GL] Shader compile error:\n", log);
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}
function createProgram(gl, vsrc, fsrc) {
  const vs = createShader(gl, gl.VERTEX_SHADER, vsrc);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, fsrc);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  const ok = gl.getProgramParameter(prog, gl.LINK_STATUS);
  if (!ok) {
    const log = gl.getProgramInfoLog(prog) || "";
    console.error("[GL] Program link error:\n", log);
    gl.deleteProgram(prog);
    return null;
  }
  return prog;
}
function createTextureFromImage2(gl, image) {
  const texture = gl.createTexture();
  if (!texture) return null;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return texture;
}
async function loadTexture2(gl, url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      resolve(createTextureFromImage2(gl, img));
    };
    img.onerror = () => {
      console.error("[GL] Failed to load texture:", url);
      resolve(null);
    };
    img.src = url;
  });
}

// src/gpu/gl/program.ts
var GLProgram = class {
  constructor(gl) {
    this.gl = gl;
    this.a_position = -1;
    this.a_uv = -1;
    this.u_resolution = null;
    this.u_translate = null;
    this.u_scale = null;
    this.u_tex0 = null;
    this.u_alpha = null;
    this.u_strength = null;
    this.u_tilt = null;
    this.u_zoom = null;
    this.u_reveal = null;
    this.u_texAspect = null;
    this.u_containerAspect = null;
    this.u_projectionMatrix = null;
    this.u_modelViewMatrix = null;
    if (!gl) {
      this.prog = null;
      return;
    }
    this.prog = createProgram(gl, IMAGE_SHADER.vertex, IMAGE_SHADER.fragment);
    if (!this.prog) {
      console.error("[GL] Failed to create shader program.");
      return;
    }
    this.a_position = gl.getAttribLocation(this.prog, "a_position");
    this.a_uv = gl.getAttribLocation(this.prog, "a_uv");
    this.u_resolution = gl.getUniformLocation(this.prog, "u_resolution");
    this.u_translate = gl.getUniformLocation(this.prog, "u_translate");
    this.u_scale = gl.getUniformLocation(this.prog, "u_scale");
    this.u_tex0 = gl.getUniformLocation(this.prog, "u_tex0");
    this.u_alpha = gl.getUniformLocation(this.prog, "u_alpha");
    this.u_strength = gl.getUniformLocation(this.prog, "u_strength");
    this.u_tilt = gl.getUniformLocation(this.prog, "u_tilt");
    this.u_zoom = gl.getUniformLocation(this.prog, "u_zoom");
    this.u_reveal = gl.getUniformLocation(this.prog, "u_reveal");
    this.u_texAspect = gl.getUniformLocation(this.prog, "u_texAspect");
    this.u_containerAspect = gl.getUniformLocation(
      this.prog,
      "u_containerAspect"
    );
    this.u_projectionMatrix = gl.getUniformLocation(
      this.prog,
      "projectionMatrix"
    );
    this.u_modelViewMatrix = gl.getUniformLocation(
      this.prog,
      "modelViewMatrix"
    );
  }
  use() {
    if (this.gl && this.prog) this.gl.useProgram(this.prog);
  }
  destroy() {
    if (this.gl && this.prog) this.gl.deleteProgram(this.prog);
  }
};

// src/gpu/gl/renderer.ts
var GLRenderer = class {
  constructor(gl, config = {}) {
    this.gl = gl;
    this.program = new GLProgram(gl);
    this.geometry = new GLGeometry(gl);
    this.camera = new Camera();
    this.maxPixelRatio = config.maxPixelRatio ?? 2;
    this.canvasSize = { width: 0, height: 0 };
    this.devicePixelRatio = 1;
    this.needsRedraw = true;
    this.extraFrames = 0;
    this.boundsFrozen = false;
    this.boundsUpdate = {
      mode: "always",
      interval: 0,
      last: 0,
      force: false
    };
    if (gl) {
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(
        gl.SRC_ALPHA,
        gl.ONE_MINUS_SRC_ALPHA,
        gl.ONE,
        gl.ONE_MINUS_SRC_ALPHA
      );
    }
  }
  /**
   * Get the camera instance
   */
  getCamera() {
    return this.camera;
  }
  /**
   * Request bounds update on next frame
   */
  requestBoundsUpdate() {
    this.boundsUpdate.force = true;
    this.needsRedraw = true;
    this.extraFrames = Math.max(this.extraFrames || 0, 2);
  }
  /**
   * Set bounds update mode
   */
  setBoundsUpdateMode(mode) {
    this.boundsUpdate.mode = mode === "always" ? "always" : "throttled";
  }
  /**
   * Set bounds update interval (for throttled mode)
   */
  setBoundsUpdateInterval(ms) {
    this.boundsUpdate.interval = Math.max(0, ms | 0);
  }
  maybeUpdateBounds(scene) {
    if (this.boundsFrozen) return;
    const now = performance.now();
    const cfg = this.boundsUpdate;
    const should = cfg.mode === "always" || cfg.force || now - cfg.last >= cfg.interval;
    if (should) {
      try {
        scene?.updateBounds?.();
      } catch {
      }
      cfg.last = now;
      cfg.force = false;
    }
  }
  /**
   * Resize the renderer to match canvas size
   */
  resize() {
    const { gl } = this;
    if (!gl) return;
    const canvas = gl.canvas;
    const cssWidth = Math.floor(canvas.clientWidth);
    const cssHeight = Math.floor(canvas.clientHeight);
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(
      rawDpr,
      cssWidth < 600 ? 3 : cssWidth > 1920 ? 1.5 : this.maxPixelRatio
    );
    const pixelWidth = Math.floor(cssWidth * dpr);
    const pixelHeight = Math.floor(cssHeight * dpr);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      this.canvasSize.width = pixelWidth;
      this.canvasSize.height = pixelHeight;
      this.devicePixelRatio = dpr;
      gl.viewport(0, 0, pixelWidth, pixelHeight);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      this.needsRedraw = true;
      this.camera.resize({ width: pixelWidth, height: pixelHeight });
    }
  }
  /**
   * Render a single scene
   */
  renderScene(scene) {
    const { gl, program } = this;
    if (!gl) return;
    if (!this.needsRedraw && this.extraFrames === 0) return;
    this.needsRedraw = false;
    this.extraFrames = Math.max(0, this.extraFrames - 1);
    gl.clearColor(0.06, 0.07, 0.09, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    program.use();
    if (program.u_projectionMatrix) {
      gl.uniformMatrix4fv(
        program.u_projectionMatrix,
        false,
        this.camera.projection
      );
    }
    if (program.u_modelViewMatrix) {
      gl.uniformMatrix4fv(program.u_modelViewMatrix, false, this.camera.view);
    }
    if (program.u_resolution) {
      gl.uniform2f(program.u_resolution, gl.canvas.width, gl.canvas.height);
    }
    this.maybeUpdateBounds(scene);
    this.drawScenePass(scene, 1);
  }
  /**
   * Render two scenes with crossfade
   */
  renderPair(fromScene, toScene, progress) {
    const { gl, program } = this;
    if (!gl) return;
    if (!this.needsRedraw && this.extraFrames === 0) return;
    this.needsRedraw = false;
    this.extraFrames = Math.max(0, this.extraFrames - 1);
    gl.clearColor(0.06, 0.07, 0.09, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    program.use();
    if (program.u_projectionMatrix) {
      gl.uniformMatrix4fv(
        program.u_projectionMatrix,
        false,
        this.camera.projection
      );
    }
    if (program.u_modelViewMatrix) {
      gl.uniformMatrix4fv(program.u_modelViewMatrix, false, this.camera.view);
    }
    if (program.u_resolution) {
      gl.uniform2f(program.u_resolution, gl.canvas.width, gl.canvas.height);
    }
    this.maybeUpdateBounds(fromScene);
    this.maybeUpdateBounds(toScene);
    const p = Math.min(1, Math.max(0, progress || 0));
    this.drawScenePass(fromScene, 1 - p);
    this.drawScenePass(toScene, p);
  }
  /**
   * Draw a scene pass with given alpha
   */
  drawScenePass(scene, alpha) {
    const { gl, program, geometry } = this;
    if (!gl) return;
    const dpr = this.devicePixelRatio || 1;
    const planes = scene.planes.slice().sort((a, b) => {
      const za = a.bounds?.z ?? 1;
      const zb = b.bounds?.z ?? 1;
      return za - zb;
    });
    for (const plane of planes) {
      let tx;
      let ty;
      let tz;
      let scaleW;
      let scaleH;
      if (plane.bounds) {
        scaleW = Math.max(1, Math.round(plane.bounds.w * dpr));
        scaleH = Math.max(1, Math.round(plane.bounds.h * dpr));
        tx = Math.round(plane.bounds.x * dpr);
        ty = Math.round(plane.bounds.y * dpr);
        tz = plane.bounds.z;
      } else {
        scaleW = Math.min(gl.canvas.width * 0.5, 640);
        scaleH = scaleW / (plane.aspectRatio || 1);
        tx = (gl.canvas.width - scaleW) * 0.5;
        ty = (gl.canvas.height - scaleH) * 0.5;
        tz = 1;
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, plane.texture ?? null);
      gl.uniform1i(program.u_tex0, 0);
      const op = plane.opacity ?? 1;
      gl.uniform1f(program.u_alpha, alpha * op);
      gl.uniform3f(program.u_translate, tx, ty, tz);
      gl.uniform2f(program.u_scale, scaleW, scaleH);
      if (program.u_texAspect) {
        gl.uniform1f(program.u_texAspect, plane.aspectRatio || 1);
      }
      if (program.u_containerAspect) {
        gl.uniform1f(program.u_containerAspect, scaleW / scaleH);
      }
      const targetS = plane.strength ?? 0;
      const prevS = plane.strengthEased ?? 0;
      const easedS = prevS + (targetS - prevS) * 0.09;
      plane.strengthEased = easedS;
      if (program.u_strength) gl.uniform1f(program.u_strength, easedS);
      if (Math.abs(easedS - targetS) > 5e-4) {
        this.needsRedraw = true;
        this.extraFrames = Math.max(this.extraFrames, 1);
      }
      const targetTilt = plane.hoverTilt ?? 0;
      const prevTilt = plane.hoverTiltEased ?? 0;
      const easedTilt = prevTilt + (targetTilt - prevTilt) * 0.12;
      plane.hoverTiltEased = easedTilt;
      if (program.u_tilt) gl.uniform1f(program.u_tilt, easedTilt);
      if (Math.abs(easedTilt - targetTilt) > 5e-4) {
        this.needsRedraw = true;
        this.extraFrames = Math.max(this.extraFrames, 1);
      }
      const targetZoom = plane.hoverZoom ?? 0;
      const prevZoom = plane.hoverZoomEased ?? 0;
      const easedZoom = prevZoom + (targetZoom - prevZoom) * 0.12;
      plane.hoverZoomEased = easedZoom;
      if (program.u_zoom) gl.uniform1f(program.u_zoom, easedZoom);
      if (Math.abs(easedZoom - targetZoom) > 5e-4) {
        this.needsRedraw = true;
        this.extraFrames = Math.max(this.extraFrames, 1);
      }
      const targetReveal = plane.bounds ? plane.reveal ?? 0 : 1;
      const prevReveal = plane.revealEased ?? 0;
      const easedReveal = prevReveal + (targetReveal - prevReveal) * 0.18;
      plane.revealEased = easedReveal;
      if (program.u_reveal) gl.uniform1f(program.u_reveal, easedReveal);
      if (Math.abs(easedReveal - targetReveal) > 5e-4) {
        this.needsRedraw = true;
        this.extraFrames = Math.max(this.extraFrames, 1);
      }
      geometry.draw(program);
    }
  }
  /**
   * Request a redraw on next frame
   */
  requestRedraw() {
    this.needsRedraw = true;
    this.extraFrames = Math.max(this.extraFrames, 2);
  }
  /**
   * Clean up resources
   */
  destroy() {
    this.geometry.destroy();
    this.program.destroy();
  }
};

// src/gpu/video.ts
function isHlsUrl(url) {
  return /\.m3u8(\?|$)/i.test(url);
}
var _nativeHls = null;
function nativeHls() {
  if (_nativeHls === null) {
    const v = document.createElement("video");
    _nativeHls = v.canPlayType("application/vnd.apple.mpegurl") !== "";
  }
  return _nativeHls;
}
function hlsSupported() {
  return typeof MediaSource !== "undefined";
}
var VideoTexture = class {
  constructor(device, options = {}) {
    /** Current GPUTexture (null until first frame is uploaded) */
    this.texture = null;
    /** Video aspect ratio (width / height), updated on metadata load */
    this.aspectRatio = 1;
    this._active = false;
    this._destroyed = false;
    /** requestVideoFrameCallback handle or RAF id */
    this.frameHandle = null;
    /** Callback fired each time a new frame is uploaded */
    this.onFrameCb = null;
    /** Callback fired when frame upload fails (browser can't copy video to GPU) */
    this.onErrorCb = null;
    /** hls.js instance for HLS streams on non-Safari browsers */
    this._hls = null;
    /** True once a canvas thumbnail has been uploaded as a static fallback */
    this._thumbnailUploaded = false;
    this.device = device;
    if (options.element) {
      this.video = options.element;
      this._external = true;
    } else {
      const {
        loop = true,
        muted = true,
        volume = 1,
        crossOrigin = "anonymous"
      } = options;
      const video = document.createElement("video");
      video.playsInline = true;
      video.loop = loop;
      video.muted = muted;
      video.volume = volume;
      video.crossOrigin = crossOrigin;
      video.preload = "auto";
      video.style.position = "absolute";
      video.style.width = "0";
      video.style.height = "0";
      video.style.opacity = "0";
      video.style.pointerEvents = "none";
      this.video = video;
      this._external = false;
    }
    this.hasRVFC = "requestVideoFrameCallback" in HTMLVideoElement.prototype;
  }
  /** True while video frames are being pumped to the GPU */
  get active() {
    return this._active;
  }
  // ==================== Initialization ====================
  /**
   * Initialize the GPU texture from the current video element.
   *
   * For external elements that already have their src set in the DOM.
   * Waits for metadata if not yet available, then creates the GPU texture.
   */
  async init(options = {}) {
    if (this._destroyed) throw new Error("VideoTexture has been destroyed");
    const src = this.video.currentSrc || this.video.src || this.video.getAttribute("src") || "";
    if (src && isHlsUrl(src) && !nativeHls() && hlsSupported()) {
      await this._attachHls(src);
    }
    return new Promise((resolve, reject) => {
      const tryResolve = () => {
        const w = this.video.videoWidth;
        const h = this.video.videoHeight;
        if (w === 0 || h === 0) {
          reject(new Error("Video element has 0 dimensions"));
          return;
        }
        this.aspectRatio = w / h;
        this.createTexture(w, h, options.label);
        if (options.autoplay) {
          this.play();
        }
        resolve(this);
      };
      if (this.video.readyState >= 1) {
        tryResolve();
      } else {
        const onMeta = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          tryResolve();
        };
        const onError = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          reject(new Error("Video element failed to load"));
        };
        this.video.addEventListener("loadedmetadata", onMeta);
        this.video.addEventListener("error", onError);
      }
    });
  }
  /**
   * Load a video from a URL.
   *
   * Resolves once video metadata is available and the first frame
   * can be drawn. Rejects if the video fails to load.
   */
  async load(src, options = {}) {
    if (this._destroyed) throw new Error("VideoTexture has been destroyed");
    const { autoplay = false } = options;
    if (options.loop !== void 0) this.video.loop = options.loop;
    if (options.muted !== void 0) this.video.muted = options.muted;
    if (options.volume !== void 0) this.video.volume = options.volume;
    if (isHlsUrl(src) && !nativeHls() && hlsSupported()) {
      await this._attachHls(src);
    } else {
      this.video.src = src;
    }
    return new Promise((resolve, reject) => {
      const tryResolve = () => {
        const w = this.video.videoWidth;
        const h = this.video.videoHeight;
        if (w === 0 || h === 0) {
          reject(new Error(`Video has 0 dimensions: ${src}`));
          return;
        }
        this.aspectRatio = w / h;
        this.createTexture(w, h, options.label);
        if (autoplay) {
          this.play();
        }
        resolve(this);
      };
      if (this.video.readyState >= 1) {
        tryResolve();
      } else {
        const onMeta = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          tryResolve();
        };
        const onError = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          reject(new Error(`Failed to load video: ${src}`));
        };
        this.video.addEventListener("loadedmetadata", onMeta);
        this.video.addEventListener("error", onError);
      }
    });
  }
  /**
   * Attach an existing HTMLVideoElement instead of loading from URL.
   *
   * The video should already have its src set. Waits for metadata
   * if not yet available.
   */
  async fromElement(video, options = {}) {
    if (this._destroyed) throw new Error("VideoTexture has been destroyed");
    const src = video.currentSrc || video.src;
    if (!src) throw new Error("Video element has no source");
    if (options.loop !== void 0) this.video.loop = options.loop;
    if (options.muted !== void 0) this.video.muted = options.muted;
    if (options.volume !== void 0) this.video.volume = options.volume;
    if (isHlsUrl(src) && !nativeHls() && hlsSupported()) {
      await this._attachHls(src);
    } else {
      this.video.src = src;
    }
    return new Promise((resolve, reject) => {
      const tryResolve = () => {
        const w = this.video.videoWidth;
        const h = this.video.videoHeight;
        if (w === 0 || h === 0) {
          reject(new Error("Video element has 0 dimensions"));
          return;
        }
        this.aspectRatio = w / h;
        this.createTexture(w, h, options.label);
        resolve(this);
      };
      if (this.video.readyState >= 1) {
        tryResolve();
      } else {
        const onMeta = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          tryResolve();
        };
        const onError = () => {
          this.video.removeEventListener("loadedmetadata", onMeta);
          this.video.removeEventListener("error", onError);
          reject(new Error("Video element failed to load"));
        };
        this.video.addEventListener("loadedmetadata", onMeta);
        this.video.addEventListener("error", onError);
      }
    });
  }
  // ==================== Playback Controls ====================
  play() {
    if (this._destroyed) return;
    this.video.play().catch(() => {
    });
    this.startFrameLoop();
  }
  pause() {
    this.video.pause();
    this.stopFrameLoop();
  }
  /** Seek to a time in seconds */
  seek(time) {
    this.video.currentTime = time;
  }
  get paused() {
    return this.video.paused;
  }
  get currentTime() {
    return this.video.currentTime;
  }
  get duration() {
    return this.video.duration;
  }
  get loop() {
    return this.video.loop;
  }
  set loop(value) {
    this.video.loop = value;
  }
  get muted() {
    return this.video.muted;
  }
  set muted(value) {
    this.video.muted = value;
  }
  get volume() {
    return this.video.volume;
  }
  set volume(value) {
    this.video.volume = value;
  }
  // ==================== Frame Callback ====================
  /**
   * Register a callback fired after each frame is uploaded to the GPU.
   * Useful for requesting renderer redraws.
   */
  onFrame(cb) {
    this.onFrameCb = cb;
  }
  /**
   * Register a callback fired when copyExternalImageToTexture fails.
   * Used to fall back to native <video> on browsers with partial WebGPU support.
   */
  onError(cb) {
    this.onErrorCb = cb;
  }
  // ==================== Internal ====================
  createTexture(w, h, label) {
    if (this.texture) {
      this.texture.destroy();
    }
    this.texture = this.device.createTexture({
      label: label ?? "Video Texture",
      size: [w, h, 1],
      format: "rgba8unorm",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT
    });
    const clearData = new Uint8Array(w * h * 4);
    this.device.queue.writeTexture(
      { texture: this.texture },
      clearData,
      { bytesPerRow: w * 4 },
      [w, h]
    );
  }
  uploadFrame() {
    if (this._destroyed || !this.texture || this._thumbnailUploaded) return;
    if (this.video.readyState < 2) return;
    const w = this.video.videoWidth;
    const h = this.video.videoHeight;
    try {
      this.device.queue.copyExternalImageToTexture(
        { source: this.video },
        { texture: this.texture },
        [w, h]
      );
    } catch {
      this.uploadThumbnail(w, h);
      return;
    }
    if (this.onFrameCb) this.onFrameCb();
  }
  /** Seek to the midpoint of the video, grab one frame via canvas, upload it, and stop. */
  uploadThumbnail(w, h) {
    this.stopFrameLoop();
    const mid = (this.video.duration || 0) / 2;
    if (!isFinite(mid) || mid <= 0) {
      this._captureThumbnail(w, h);
      return;
    }
    this.video.currentTime = mid;
    this.video.addEventListener("seeked", () => this._captureThumbnail(w, h), {
      once: true
    });
  }
  _captureThumbnail(w, h) {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      if (this.onErrorCb) this.onErrorCb();
      return;
    }
    ctx.drawImage(this.video, 0, 0, w, h);
    try {
      this.device.queue.copyExternalImageToTexture(
        { source: canvas },
        { texture: this.texture },
        [w, h]
      );
    } catch {
      if (this.onErrorCb) this.onErrorCb();
      return;
    }
    this._thumbnailUploaded = true;
    this.video.pause();
    if (this.onFrameCb) this.onFrameCb();
  }
  startFrameLoop() {
    if (this._active) return;
    this._active = true;
    if (this.hasRVFC) {
      this.rvfcLoop();
    } else {
      this.rafLoop();
    }
  }
  stopFrameLoop() {
    this._active = false;
    if (this.frameHandle !== null) {
      if (this.hasRVFC) {
        this.video.cancelVideoFrameCallback(this.frameHandle);
      } else {
        cancelAnimationFrame(this.frameHandle);
      }
      this.frameHandle = null;
    }
  }
  /** requestVideoFrameCallback loop - only runs when video produces a frame */
  rvfcLoop() {
    if (!this._active || this._destroyed) return;
    this.frameHandle = this.video.requestVideoFrameCallback(() => {
      this.uploadFrame();
      this.rvfcLoop();
    });
  }
  /** requestAnimationFrame fallback - polls every display frame */
  rafLoop() {
    if (!this._active || this._destroyed) return;
    this.frameHandle = requestAnimationFrame(() => {
      if (!this.video.paused && !this.video.ended) {
        this.uploadFrame();
      }
      this.rafLoop();
    });
  }
  // ==================== HLS ====================
  /**
   * Attach hls.js to the video element for HLS playback via MSE.
   * Resolves once the manifest is parsed and the video has metadata.
   */
  async _attachHls(src) {
    const { default: Hls } = await import('hls.js');
    return new Promise((resolve, reject) => {
      const hls = new Hls();
      this._hls = hls;
      const cleanup = () => {
        hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
        hls.off(Hls.Events.ERROR, onError);
      };
      const onParsed = () => {
        cleanup();
        if (this.video.readyState >= 1) {
          resolve();
        } else {
          const onMeta = () => {
            this.video.removeEventListener("loadedmetadata", onMeta);
            resolve();
          };
          this.video.addEventListener("loadedmetadata", onMeta);
        }
      };
      const onError = (_event, data) => {
        if (data.fatal) {
          cleanup();
          hls.destroy();
          this._hls = null;
          reject(new Error(`HLS error: ${data.details}`));
        }
      };
      hls.on(Hls.Events.MANIFEST_PARSED, onParsed);
      hls.on(Hls.Events.ERROR, onError);
      hls.loadSource(src);
      hls.attachMedia(this.video);
    });
  }
  // ==================== Cleanup ====================
  /**
   * Stop playback, release GPU texture, and clean up.
   *
   * For external elements: only stops the frame loop and destroys the
   * GPU texture. Does not modify the video element (we don't own it).
   *
   * For internal elements: also strips the src and releases media resources.
   */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    this.stopFrameLoop();
    this.onFrameCb = null;
    this.onErrorCb = null;
    this.video.pause();
    if (this._hls) {
      this._hls.destroy();
      this._hls = null;
    }
    if (!this._external) {
      this.video.removeAttribute("src");
      this.video.load();
    }
    if (this.texture) {
      this.texture.destroy();
      this.texture = null;
    }
  }
};

// src/gpu/post/post-pass.ts
var PostPass = class {
  constructor(device, format, config) {
    this.name = config.name;
    this.enabled = config.enabled ?? true;
    this.needsSwap = config.needsSwap ?? true;
    const uniformBufferSize = Math.max(config.uniformBufferSize ?? 64, 16);
    const shaderModule = device.createShaderModule({
      label: `${config.name} Shader`,
      code: config.shaderSource
    });
    this.bindGroupLayout = device.createBindGroupLayout({
      label: `${config.name} Bind Group Layout`,
      entries: [
        {
          // Uniforms
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" }
        },
        {
          // Sampler
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: "filtering" }
        },
        {
          // Texture
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" }
        }
      ]
    });
    const pipelineLayout = device.createPipelineLayout({
      label: `${config.name} Pipeline Layout`,
      bindGroupLayouts: [this.bindGroupLayout]
    });
    this.pipeline = device.createRenderPipeline({
      label: `${config.name} Pipeline`,
      layout: pipelineLayout,
      vertex: {
        module: shaderModule,
        entryPoint: "vs"
      },
      fragment: {
        module: shaderModule,
        entryPoint: "fs",
        targets: [
          {
            format,
            blend: {
              color: {
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
                operation: "add"
              },
              alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add"
              }
            },
            writeMask: GPUColorWrite.ALL
          }
        ]
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none"
      }
    });
    this.uniformBuffer = device.createBuffer({
      label: `${config.name} Uniform Buffer`,
      size: uniformBufferSize,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });
  }
  /**
   * Create bind group for this pass with given input texture
   */
  createBindGroup(device, sampler, inputView) {
    return device.createBindGroup({
      label: `${this.name} Bind Group`,
      layout: this.bindGroupLayout,
      entries: [
        {
          binding: 0,
          resource: { buffer: this.uniformBuffer }
        },
        {
          binding: 1,
          resource: sampler
        },
        {
          binding: 2,
          resource: inputView
        }
      ]
    });
  }
  /**
   * Update uniform buffer with new values
   */
  updateUniforms(device, data) {
    device.queue.writeBuffer(this.uniformBuffer, 0, data);
  }
  /**
   * Destroy GPU resources
   */
  destroy() {
    this.uniformBuffer.destroy();
  }
};

// src/gpu/post/post-processor.ts
var PostProcessor = class {
  constructor(config) {
    this.passes = [];
    this.device = config.device;
    this.format = config.format;
    this.width = Math.max(1, config.width);
    this.height = Math.max(1, config.height);
    this.sampler = this.device.createSampler({
      label: "PostProcessor Sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge"
    });
    this.buffers = this.createPingPongBuffers();
  }
  createPingPongBuffers() {
    const createTexture = (label) => {
      return this.device.createTexture({
        label,
        size: [this.width, this.height],
        format: this.format,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });
    };
    let readTex = createTexture("PostProcessor Read Buffer");
    let writeTex = createTexture("PostProcessor Write Buffer");
    let readView = readTex.createView();
    let writeView = writeTex.createView();
    return {
      read: readTex,
      write: writeTex,
      readView,
      writeView,
      swap() {
        const tempTex = readTex;
        const tempView = readView;
        readTex = writeTex;
        readView = writeView;
        writeTex = tempTex;
        writeView = tempView;
        this.read = readTex;
        this.write = writeTex;
        this.readView = readView;
        this.writeView = writeView;
      }
    };
  }
  /**
   * Add a pass to the chain
   * Returns the pass for further configuration
   */
  addPass(config) {
    const pass = new PostPass(this.device, this.format, config);
    this.passes.push(pass);
    return pass;
  }
  /**
   * Remove a pass by name
   */
  removePass(name) {
    const index = this.passes.findIndex((p) => p.name === name);
    if (index === -1) return false;
    this.passes[index].destroy();
    this.passes.splice(index, 1);
    return true;
  }
  /**
   * Get a pass by name
   */
  getPass(name) {
    return this.passes.find((p) => p.name === name);
  }
  /**
   * Render all enabled passes
   * @param encoder - Command encoder
   * @param inputView - Scene render result texture view
   * @param outputView - Canvas texture view (final output) OR null to keep in read buffer
   */
  render(encoder, inputView, outputView) {
    const enabledPasses = this.passes.filter((p) => p.enabled);
    if (enabledPasses.length === 0) {
      return;
    }
    let currentInput = inputView;
    for (let i = 0; i < enabledPasses.length; i++) {
      const pass = enabledPasses[i];
      const isLast = i === enabledPasses.length - 1;
      const target = isLast && outputView ? outputView : this.buffers.writeView;
      this.renderPass(encoder, pass, currentInput, target, isLast);
      if (!isLast && pass.needsSwap) {
        this.buffers.swap();
        currentInput = this.buffers.readView;
      }
    }
  }
  renderPass(encoder, pass, inputView, outputView, _isLast) {
    const bindGroup = pass.createBindGroup(
      this.device,
      this.sampler,
      inputView
    );
    const renderPass = encoder.beginRenderPass({
      label: `${pass.name} Render Pass`,
      colorAttachments: [
        {
          view: outputView,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 0 }
        }
      ]
    });
    renderPass.setPipeline(pass.pipeline);
    renderPass.setBindGroup(0, bindGroup);
    renderPass.draw(3);
    renderPass.end();
  }
  /**
   * Resize render targets (call on canvas resize)
   */
  resize(width, height) {
    const newWidth = Math.max(1, width);
    const newHeight = Math.max(1, height);
    if (this.width === newWidth && this.height === newHeight) return;
    this.width = newWidth;
    this.height = newHeight;
    this.buffers.read.destroy();
    this.buffers.write.destroy();
    this.buffers = this.createPingPongBuffers();
  }
  /**
   * Clean up all resources
   */
  destroy() {
    for (const pass of this.passes) {
      pass.destroy();
    }
    this.passes = [];
    this.buffers.read.destroy();
    this.buffers.write.destroy();
  }
};

// src/gpu/post/shaders/passthrough.wgsl?raw
var passthrough_default = "// Passthrough Post-Processing Shader\n// Renders input texture to output unchanged\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  _pad: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  return textureSample(tex, smp, input.texcoord);\n}\n";

// src/gpu/post/shaders/blur.wgsl?raw
var blur_default = "// Separable Gaussian Blur Shader\n// Use two passes: horizontal (direction = vec2f(1,0)) then vertical (direction = vec2f(0,1))\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  strength: f32,      // Blur strength multiplier (1.0 = normal, 2.0 = stronger)\n  direction: vec2f,   // (1,0) for horizontal, (0,1) for vertical\n  mouseMode: f32,     // 0: global, 1: mouse-driven (blur away from cursor, sharp at cursor)\n  mouse: vec2f,       // Mouse position in UV space (0-1)\n  radius: f32,        // Effect radius for mouse mode\n  _pad: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Precomputed 9-tap Gaussian weights (sigma ~= 2)\nconst WEIGHTS = array<f32, 5>(0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216);\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let texelSize = 1.0 / uniforms.resolution;\n  \n  var strength = uniforms.strength;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: sharp at cursor, blurry away from cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = smoothstep(0.0, uniforms.radius, dist);\n    strength = uniforms.strength * falloff * 2.0;\n  }\n  \n  let dir = uniforms.direction * texelSize * strength;\n\n  // Center sample\n  var result = textureSample(tex, smp, input.texcoord) * WEIGHTS[0];\n\n  // Symmetric samples\n  for (var i = 1; i < 5; i++) {\n    let offset = dir * f32(i);\n    result += textureSample(tex, smp, input.texcoord + offset) * WEIGHTS[i];\n    result += textureSample(tex, smp, input.texcoord - offset) * WEIGHTS[i];\n  }\n\n  return result;\n}\n";

// src/gpu/post/shaders/bloom.wgsl?raw
var bloom_default = "// Bloom Post-Processing Shader\n// Extracts bright areas using luminance threshold and adds glow effect\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  threshold: f32,   // Brightness threshold (0.0-1.0)\n  intensity: f32,   // Bloom intensity multiplier\n  mouseMode: f32,   // 0: global, 1: mouse-driven (bloom centered on cursor)\n  mouse: vec2f,     // Mouse position in UV space (0-1)\n  radius: f32,      // Effect radius for mouse mode\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n// Calculate luminance using standard coefficients\nfn luminance(color: vec3f) -> f32 {\n  return dot(color, vec3f(0.2126, 0.7152, 0.0722));\n}\n\n// Extract bright pixels above threshold with soft falloff\nfn extractBright(color: vec4f, threshold: f32) -> vec4f {\n  let lum = luminance(color.rgb);\n  // Soft threshold using smoothstep for gradual falloff\n  let bright = smoothstep(threshold, threshold + 0.1, lum);\n  return vec4f(color.rgb * bright, color.a);\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let original = textureSample(tex, smp, input.texcoord);\n  \n  var intensity = uniforms.intensity;\n  var threshold = uniforms.threshold;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: bloom intensity based on distance from cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = 1.0 - smoothstep(0.0, uniforms.radius, dist);\n    intensity = uniforms.intensity * falloff * 2.0;\n    // Lower threshold near mouse for more glow\n    threshold = mix(uniforms.threshold, uniforms.threshold * 0.5, falloff);\n  }\n  \n  let bright = extractBright(original, threshold);\n  \n  // Additive blend: original + (bright * intensity)\n  let result = original.rgb + bright.rgb * intensity;\n  \n  return vec4f(result, original.a);\n}\n";

// src/gpu/post/shaders/pixelate.wgsl?raw
var pixelate_default = "// Pixelate Post-Processing Shader\n// Reduces resolution to create blocky retro appearance\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  pixelSize: f32,   // Size of pixelated blocks (higher = blockier)\n  mouseMode: f32,   // 0: global, 1: mouse-driven (pixelate near cursor)\n  mouse: vec2f,     // Mouse position in UV space (0-1)\n  radius: f32,      // Effect radius for mouse mode\n  _pad: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  var pixelSize = uniforms.pixelSize;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: pixelate near cursor, sharp away from cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = 1.0 - smoothstep(0.0, uniforms.radius, dist);\n    // Interpolate between 1 (no pixelation) and full pixelSize\n    pixelSize = mix(1.0, uniforms.pixelSize, falloff);\n  }\n  \n  // If pixelSize is 1 or less, just return original\n  if (pixelSize <= 1.0) {\n    return textureSample(tex, smp, input.texcoord);\n  }\n  \n  // Calculate pixel grid size in UV space\n  let gridSize = pixelSize / uniforms.resolution;\n  \n  // Snap UV coordinates to grid using floor\n  let snappedUV = floor(input.texcoord / gridSize) * gridSize;\n  \n  // Sample at center of pixel block\n  let centeredUV = snappedUV + gridSize * 0.5;\n  \n  return textureSample(tex, smp, centeredUV);\n}\n";

// src/gpu/post/shaders/grayscale.wgsl?raw
var grayscale_default = "// Grayscale Post-Processing Shader\n// Converts color to black and white with configurable mix\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  amount: f32,      // 0.0 = original, 1.0 = full grayscale\n  mouseMode: f32,   // 0: global, 1: mouse-driven (grayscale near cursor)\n  mouse: vec2f,     // Mouse position in UV space (0-1)\n  radius: f32,      // Effect radius for mouse mode\n  _pad: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// ITU-R BT.709 luminance coefficients\nfn luminance(color: vec3f) -> f32 {\n  return dot(color, vec3f(0.2126, 0.7152, 0.0722));\n}\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let color = textureSample(tex, smp, input.texcoord);\n  let gray = luminance(color.rgb);\n  let grayscaleColor = vec3f(gray);\n  \n  var amount = uniforms.amount;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: grayscale near cursor, color away from cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = 1.0 - smoothstep(0.0, uniforms.radius, dist);\n    amount = uniforms.amount * falloff;\n  }\n  \n  let result = mix(color.rgb, grayscaleColor, amount);\n  return vec4f(result, color.a);\n}\n";

// src/gpu/post/shaders/noise.wgsl?raw
var noise_default = "// Noise Post-Processing Shader\n// Adds film grain effect with configurable intensity\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  amount: f32,      // 0.0 = no noise, 1.0 = full noise\n  mouseMode: f32,   // 0: global, 1: mouse-driven (noise near cursor)\n  mouse: vec2f,     // Mouse position in UV space (0-1)\n  radius: f32,      // Effect radius for mouse mode\n  _pad: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// GPU-friendly hash function for noise\nfn hash(p: vec2f) -> f32 {\n  var p3 = fract(vec3f(p.xyx) * 0.1031);\n  p3 += dot(p3, p3.yzx + 33.33);\n  return fract((p3.x + p3.y) * p3.z);\n}\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let color = textureSample(tex, smp, input.texcoord);\n  \n  // Time-animated noise for film grain effect\n  let pixelCoord = input.texcoord * uniforms.resolution;\n  let noise = hash(pixelCoord + uniforms.time * 100.0);\n  \n  var amount = uniforms.amount;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: noise near cursor, clean away from cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = 1.0 - smoothstep(0.0, uniforms.radius, dist);\n    amount = uniforms.amount * falloff;\n  }\n  \n  // Center noise around 0 (-0.5 to 0.5) for balanced grain\n  let grain = (noise - 0.5) * amount;\n  \n  let result = color.rgb + vec3f(grain);\n  return vec4f(result, color.a);\n}\n";

// src/gpu/post/shaders/rgb-split.wgsl?raw
var rgb_split_default = "// RGB Split / Chromatic Aberration Post-Processing Shader\n// Offsets R, G, B channels to create chromatic aberration effect\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  amount: f32,       // Offset strength (0.0 to ~0.05 typical)\n  angle: f32,        // Direction angle in radians\n  mouseMode: f32,    // 0: use angle, 1: radiate from mouse\n  mouse: vec2f,      // Mouse position in UV space (0-1)\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let uv = input.texcoord;\n  \n  var dir: vec2f;\n  var amount = uniforms.amount;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: RGB split radiates from mouse position\n    let toPixel = uv - uniforms.mouse;\n    let dist = length(toPixel);\n    dir = normalize(toPixel + vec2f(0.0001)); // Avoid division by zero\n    \n    // Scale amount by distance from mouse (stronger near mouse)\n    amount = uniforms.amount * (1.0 - smoothstep(0.0, 0.5, dist)) * 2.0;\n  } else {\n    // Angle mode: use fixed direction\n    dir = vec2f(cos(uniforms.angle), sin(uniforms.angle));\n  }\n  \n  let offset = dir * amount;\n  \n  // Sample each channel with offset\n  // R: positive offset, G: center, B: negative offset\n  let r = textureSample(tex, smp, uv + offset).r;\n  let g = textureSample(tex, smp, uv).g;\n  let b = textureSample(tex, smp, uv - offset).b;\n  \n  // Preserve alpha from center sample\n  let a = textureSample(tex, smp, uv).a;\n  \n  return vec4f(r, g, b, a);\n}\n";

// src/gpu/post/shaders/distortion.wgsl?raw
var distortion_default = "// Distortion Post-Processing Shader\n// Displaces pixels using various UV manipulation techniques\n\nstruct Uniforms {\n  resolution: vec2f,  // offset 0\n  time: f32,          // offset 8\n  amount: f32,        // offset 12\n  frequency: f32,     // offset 16\n  speed: f32,         // offset 20\n  mouse: vec2f,       // offset 24 (8-byte aligned)\n  mode: f32,          // offset 32\n  radius: f32,        // offset 36\n  _pad: vec2f,        // offset 40 (pad to 48 bytes)\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n// Hash function for pseudo-random noise\nfn hash(p: vec2f) -> f32 {\n  let h = dot(p, vec2f(127.1, 311.7));\n  return fract(sin(h) * 43758.5453123);\n}\n\n// 2D noise based on hash\nfn noise2d(p: vec2f) -> f32 {\n  let i = floor(p);\n  let f = fract(p);\n  \n  let a = hash(i);\n  let b = hash(i + vec2f(1.0, 0.0));\n  let c = hash(i + vec2f(0.0, 1.0));\n  let d = hash(i + vec2f(1.0, 1.0));\n  \n  let u = f * f * (3.0 - 2.0 * f);\n  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);\n}\n\n// Wave distortion: animated sine/cosine displacement\nfn waveDistort(uv: vec2f, time: f32, amount: f32, freq: f32, speed: f32) -> vec2f {\n  let t = time * speed;\n  let offsetX = sin(uv.y * freq + t) * amount;\n  let offsetY = cos(uv.x * freq + t) * amount;\n  return uv + vec2f(offsetX, offsetY);\n}\n\n// Barrel distortion: radial distortion from center\nfn barrelDistort(uv: vec2f, amount: f32) -> vec2f {\n  let centered = uv - vec2f(0.5);\n  let dist = length(centered);\n  let distorted = centered * (1.0 + amount * dist * dist);\n  return distorted + vec2f(0.5);\n}\n\n// Noise distortion: random displacement using hash function\nfn noiseDistort(uv: vec2f, time: f32, amount: f32, freq: f32, speed: f32) -> vec2f {\n  let t = time * speed;\n  let noiseCoord = uv * freq + vec2f(t, t * 0.7);\n  let n1 = noise2d(noiseCoord) * 2.0 - 1.0;\n  let n2 = noise2d(noiseCoord + vec2f(100.0, 100.0)) * 2.0 - 1.0;\n  return uv + vec2f(n1, n2) * amount;\n}\n\n// FBM (Fractal Brownian Motion) noise\nfn fbm(p: vec2f, time: f32) -> f32 {\n  var value = 0.0;\n  var amplitude = 0.5;\n  var freq = 1.0;\n  var pos = p;\n  \n  // 4 octaves\n  for (var i = 0; i < 4; i++) {\n    value += amplitude * noise2d(pos * freq + vec2f(time * 0.5, time * 0.3));\n    freq *= 2.0;\n    amplitude *= 0.5;\n    pos = pos * 1.2 + vec2f(time * 0.1);\n  }\n  \n  return value;\n}\n\n// Mouse ripple with FBM: organic distortion around mouse position\nfn mouseRipple(uv: vec2f, mouse: vec2f, time: f32, amount: f32, freq: f32, radius: f32) -> vec2f {\n  let toMouse = uv - mouse;\n  let dist = length(toMouse);\n  \n  // Falloff based on distance from mouse\n  let falloff = 1.0 - smoothstep(0.0, radius, dist);\n  \n  // FBM-based displacement\n  let noiseCoord = uv * freq;\n  let fbmX = fbm(noiseCoord, time) * 2.0 - 1.0;\n  let fbmY = fbm(noiseCoord + vec2f(43.0, 17.0), time) * 2.0 - 1.0;\n  \n  let displacement = vec2f(fbmX, fbmY) * amount * falloff;\n  \n  return uv + displacement;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  var uv = input.texcoord;\n  \n  let mode = i32(uniforms.mode);\n  \n  // Apply distortion based on mode\n  if (mode == 0) {\n    // Wave mode\n    uv = waveDistort(uv, uniforms.time, uniforms.amount, uniforms.frequency, uniforms.speed);\n  } else if (mode == 1) {\n    // Barrel mode\n    uv = barrelDistort(uv, uniforms.amount);\n  } else if (mode == 2) {\n    // Noise mode\n    uv = noiseDistort(uv, uniforms.time, uniforms.amount, uniforms.frequency, uniforms.speed);\n  } else {\n    // Mouse ripple mode (mode == 3)\n    uv = mouseRipple(uv, uniforms.mouse, uniforms.time, uniforms.amount, uniforms.frequency, uniforms.radius);\n  }\n  \n  // Clamp UV to valid range to avoid artifacts at edges\n  uv = clamp(uv, vec2f(0.0), vec2f(1.0));\n  \n  return textureSample(tex, smp, uv);\n}\n";

// src/gpu/post/shaders/adjust.wgsl?raw
var adjust_default = "// Adjust Post-Processing Shader\n// Brightness, contrast, and saturation control in one pass\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  brightness: f32,  // -1.0 to 1.0, 0.0 = no change\n  contrast: f32,    // 0.0 to 2.0, 1.0 = no change\n  saturation: f32,  // 0.0 to 2.0, 1.0 = no change\n  mouseMode: f32,   // 0: global, 1: mouse-driven (effect centered on cursor)\n  mouse: vec2f,     // Mouse position in UV space (0-1)\n  radius: f32,      // Effect radius for mouse mode\n  _pad1: f32,\n  _pad2: f32,\n  _pad3: f32,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var tex: texture_2d<f32>;\n\n// ITU-R BT.709 luminance coefficients\nfn luminance(color: vec3f) -> f32 {\n  return dot(color, vec3f(0.2126, 0.7152, 0.0722));\n}\n\n// Brightness adjustment: add value to color\nfn adjustBrightness(color: vec3f, brightness: f32) -> vec3f {\n  return color + vec3f(brightness);\n}\n\n// Contrast adjustment: scale from mid-gray\nfn adjustContrast(color: vec3f, contrast: f32) -> vec3f {\n  return (color - vec3f(0.5)) * contrast + vec3f(0.5);\n}\n\n// Saturation adjustment: mix with grayscale\nfn adjustSaturation(color: vec3f, saturation: f32) -> vec3f {\n  let gray = luminance(color);\n  return mix(vec3f(gray), color, saturation);\n}\n\n// Fullscreen triangle - no vertex buffer needed\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let color = textureSample(tex, smp, input.texcoord);\n  \n  var brightness = uniforms.brightness;\n  var contrast = uniforms.contrast;\n  var saturation = uniforms.saturation;\n  \n  if (uniforms.mouseMode > 0.5) {\n    // Mouse mode: effect strongest near cursor\n    let dist = length(input.texcoord - uniforms.mouse);\n    let falloff = 1.0 - smoothstep(0.0, uniforms.radius, dist);\n    \n    // Interpolate between normal (0, 1, 1) and effect values\n    brightness = mix(0.0, uniforms.brightness, falloff);\n    contrast = mix(1.0, uniforms.contrast, falloff);\n    saturation = mix(1.0, uniforms.saturation, falloff);\n  }\n  \n  // Apply adjustments in order: brightness -> contrast -> saturation\n  var result = adjustBrightness(color.rgb, brightness);\n  result = adjustContrast(result, contrast);\n  result = adjustSaturation(result, saturation);\n  \n  // Clamp to valid range\n  result = clamp(result, vec3f(0.0), vec3f(1.0));\n  \n  return vec4f(result, color.a);\n}\n";

// src/gpu/post/shaders/split-view.wgsl?raw
var split_view_default = "// Split View Debug Post-Processing Shader\n// Shows before/after comparison with draggable split line\n\nstruct Uniforms {\n  resolution: vec2f,\n  time: f32,\n  splitPosition: f32,  // 0.0 to 1.0, position of split line (default 0.5)\n  lineWidth: f32,      // Width of split line in pixels (default 2.0)\n  showOriginalLeft: f32, // 1.0 = original on left, 0.0 = original on right\n  _pad: vec2f,\n}\n\nstruct VSOutput {\n  @builtin(position) position: vec4f,\n  @location(0) texcoord: vec2f,\n}\n\n@group(0) @binding(0) var<uniform> uniforms: Uniforms;\n@group(0) @binding(1) var smp: sampler;\n@group(0) @binding(2) var processedTex: texture_2d<f32>;  // Post-processed result\n@group(0) @binding(3) var originalTex: texture_2d<f32>;   // Original scene\n\n@vertex fn vs(@builtin(vertex_index) vertexIndex: u32) -> VSOutput {\n  var pos = array(\n    vec2f(-1.0, -1.0),\n    vec2f(-1.0,  3.0),\n    vec2f( 3.0, -1.0),\n  );\n\n  var output: VSOutput;\n  let xy = pos[vertexIndex];\n  output.position = vec4f(xy, 0.0, 1.0);\n  output.texcoord = xy * vec2f(0.5, -0.5) + vec2f(0.5);\n  return output;\n}\n\n@fragment fn fs(input: VSOutput) -> @location(0) vec4f {\n  let processed = textureSample(processedTex, smp, input.texcoord);\n  let original = textureSample(originalTex, smp, input.texcoord);\n\n  // Calculate split line\n  let lineHalfWidth = (uniforms.lineWidth * 0.5) / uniforms.resolution.x;\n  let splitMin = uniforms.splitPosition - lineHalfWidth;\n  let splitMax = uniforms.splitPosition + lineHalfWidth;\n\n  // Check if we're on the split line\n  if (input.texcoord.x > splitMin && input.texcoord.x < splitMax) {\n    // Draw white split line\n    return vec4f(1.0, 1.0, 1.0, 1.0);\n  }\n\n  // Determine which side we're on\n  let isLeftSide = input.texcoord.x < uniforms.splitPosition;\n  let showOriginalOnLeft = uniforms.showOriginalLeft > 0.5;\n\n  if ((isLeftSide && showOriginalOnLeft) || (!isLeftSide && !showOriginalOnLeft)) {\n    return original;\n  }\n  return processed;\n}\n";

// src/gpu/post/index.ts
var PostShaders = {
  passthrough: passthrough_default,
  blur: blur_default,
  bloom: bloom_default,
  pixelate: pixelate_default,
  grayscale: grayscale_default,
  noise: noise_default,
  rgbSplit: rgb_split_default,
  distortion: distortion_default,
  adjust: adjust_default,
  splitView: split_view_default
};
var PostUniformSizes = {
  passthrough: 16,
  // resolution (8) + time (4) + pad (4)
  blur: 32,
  // resolution (8) + time (4) + strength (4) + direction (8) + pad (8)
  bloom: 32,
  // resolution (8) + time (4) + threshold (4) + intensity (4) + mode (4) + pad (8)
  pixelate: 16,
  // resolution (8) + time (4) + pixelSize (4)
  grayscale: 16,
  // resolution (8) + time (4) + amount (4)
  noise: 16,
  // resolution (8) + time (4) + amount (4)
  rgbSplit: 32,
  // resolution (8) + time (4) + amount (4) + angle (4) + pad (12)
  distortion: 48,
  // resolution (8) + time (4) + amount (4) + frequency (4) + speed (4) + mouse (8) + mode (4) + radius (4) + pad (8)
  adjust: 32,
  // resolution (8) + time (4) + brightness (4) + contrast (4) + saturation (4) + pad (4)
  splitView: 32
  // resolution (8) + time (4) + splitPos (4) + lineWidth (4) + showOrigLeft (4) + pad (8)
};

export { Camera, GLGeometry, GLProgram, GLRenderer, GPURenderer, IMAGE_SHADER, Mat4, PostPass, PostProcessor, PostShaders, PostUniformSizes, UNIFORM_STRIDE, VERTEX_BUFFER_LAYOUT, Vec3Utils, VideoTexture, createProgram as createGLProgram, createTextureFromImage2 as createGLTexture, createImagePipeline, createPlaceholderTexture, createPlaneBindGroup, createQuadGeometry, createSampler, createShader, createTextureFromImage, createUniformBuffer, destroyGPU, destroyGeometry, destroyUniformBuffer, initGPU, isWebGPUSupported, loadTexture2 as loadGLTexture, loadTexture, uploadUniforms, writeUniform };
//# sourceMappingURL=gpu.js.map
//# sourceMappingURL=gpu.js.map